import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Text } from '../../../ui/atoms';
import {
  ChartLegend,
  ChartsWrapper,
  ColorBox,
  FullDataBox,
} from '../Statistics/SupervisorStats/style';
import CircularStats from '../../../ui/atoms/CircularStats';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import { generateColorPalette } from '../../../utils/colors';
import { ChartBlock, FullPage } from './style';
import { getFilterLabel } from '../../../utils/labels';
import BarChart from '../../../ui/components/BarChart';
import {
  barChartConfigMultiSets,
  regionsProvinceLabels,
} from '../../../utils/course-state';
import {
  REGION_PROVINCE_CHART_OPTS,
  formatNumberToK,
} from '../../../utils/charts';

const UserChart = ({ mediaIsPhone, isSupervisor, filterKey, data }) => {
  const formatUsers = (users) => {
    const formattedUsers = {};
    users?.forEach((u) => {
      formattedUsers[u?.label] = u?.count;
    });
    return formattedUsers;
  };

  const [regionProvinceStatistics, setRegionProvinceStatistics] = useState({});
  useEffect(() => {
    const formattedAssignments = Object.fromEntries(
      (data?.assignments?.items ?? []).map((item) => [
        item.label,
        { assignments: item.count },
      ])
    );
    const formattedFruitions = Object.fromEntries(
      (data?.fruitions?.items ?? []).map((item) => [
        item.label,
        { fruitions: item.count },
      ])
    );
    const formattedCompletions = Object.fromEntries(
      (data?.completions?.items ?? []).map((item) => [
        item.label,
        { completions: item.count },
      ])
    );
    setRegionProvinceStatistics(
      deepMerge(
        {},
        formattedAssignments,
        formattedFruitions,
        formattedCompletions
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.assignments?.items,
    data?.completions?.items,
    data?.fruitions?.items,
  ]);

  function deepMerge(target, ...sources) {
    for (const source of sources) {
      for (const key in source) {
        if (source.hasOwnProperty(key)) {
          if (typeof source[key] === 'object' && !Array.isArray(source[key])) {
            if (!target[key]) {
              Object.assign(target, { [key]: {} });
            }
            deepMerge(target[key], source[key]);
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }
    }
    return target;
  }

  if (typeof isSupervisor === 'undefined') return null;
  if (isSupervisor && (filterKey === 'region' || filterKey === 'province')) {
    return (
      <ChartBlock>
        <Text type="h2.5" mb={16}>
          {getFilterLabel(filterKey)}
        </Text>

        <div className="barchart-wrapper">
          <BarChart
            config={barChartConfigMultiSets(regionProvinceStatistics)}
            customOptions={REGION_PROVINCE_CHART_OPTS}
          />
        </div>
        <div className="d-flex sb">
          <FullDataBox>
            <Text type="b5">Assegnazioni:</Text>
            <Text type="h3.5" color="#003366">
              {formatNumberToK(data?.assignments?.total_users)}
            </Text>
            <Text type="b5">Fruizioni:</Text>
            <Text type="h3.5" color="#003366">
              {formatNumberToK(data?.fruitions?.total_users)}
            </Text>
            <Text type="b5">Completamenti:</Text>
            <Text type="h3.5" color="#003366">
              {formatNumberToK(data?.completions?.total_users)}
            </Text>
          </FullDataBox>
          <ChartLegend>
            {regionsProvinceLabels.map((item, i) => (
              <div className="d-flex" key={`legend-item-${item}-${i}`}>
                <ColorBox
                  color={generateColorPalette(regionsProvinceLabels.length)[i]}
                />
                <Text type="b5">{item}</Text>
              </div>
            ))}
          </ChartLegend>
        </div>
      </ChartBlock>
    );
  }

  return (
    <FullPage>
      <Text type="h2.5">{getFilterLabel(filterKey)}</Text>
      <ChartsWrapper>
        <div className="d-flex sb chart-container">
          <div>
            <Text type="h3.5" mb={16}>
              Assegnazioni
            </Text>
            <Text type="b5">
              Panoramica degli utenti che visualizzano almeno uno dei tuoi
              percorsi formativi
            </Text>
          </div>
          <div className="chart-wrapper">
            <CircularStats
              label="Utenti totali"
              size={mediaIsPhone ? 190 : 130}
              data={formatUsers(data?.assignments?.items)}
              chartOnly
              mt={0}
            />
          </div>
        </div>
        <div className="d-flex sb chart-container">
          <div>
            <Text type="h3.5" mb={16}>
              Fruizioni
            </Text>
            <Text type="b5">
              Panoramica degli utenti che fruiscono di almeno uno dei tuoi
              percorsi formativi
            </Text>
          </div>
          <div className="chart-wrapper">
            <CircularStats
              label="Utenti totali"
              size={mediaIsPhone ? 190 : 130}
              data={formatUsers(data?.fruitions?.items)}
              chartOnly
              mt={0}
            />
          </div>
        </div>
        <div className="d-flex sb chart-container last">
          <div>
            <Text type="h3.5" mb={16}>
              Completamenti
            </Text>
            <Text type="b5">
              Panoramica degli utenti che hanno completato almeno uno dei tuoi
              percorsi formativi
            </Text>
          </div>
          <div className="chart-wrapper">
            <CircularStats
              label="Utenti totali"
              size={mediaIsPhone ? 190 : 130}
              data={formatUsers(data?.completions?.items)}
              chartOnly
              mt={0}
            />
          </div>
        </div>
      </ChartsWrapper>
      <ChartLegend>
        {(data?.assignments?.items || []).map((item, i) => (
          <div className="d-flex" key={`legend-item-${item?.label}-${i}`}>
            <ColorBox
              color={generateColorPalette(data?.assignments?.items?.length)[i]}
            />
            <Text type="b5">{item?.label}</Text>
          </div>
        ))}
      </ChartLegend>
    </FullPage>
  );
};

UserChart.propTypes = {
  mediaIsPhone: PropTypes.bool,
  isSupervisor: PropTypes.bool,
  filterKey: PropTypes.string.isRequired,
  data: PropTypes.object,
};

export default withMediaQueries(UserChart);
